/**
 * Notification Events Logger (SE-4010)
 *
 * Handles logging of push-notification engagement and preference-change
 * events to PostHog Analytics.
 * @module features/analytics/utils/notificationEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  INotificationAnalyticsService,
  TNotificationPreferenceUpdatedParams,
  TNotificationTappedParams,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * Notification events analytics logger.
 *
 * Provides methods for logging push-notification taps and preference
 * changes.
 * @example
 * ```typescript
 * import { AnalyticsTracker } from '@/features/analytics';
 *
 * AnalyticsTracker.notification.logNotificationTapped({
 *   activity_type: 'CONTENT_SHARED',
 * });
 * ```
 */
export const notificationEventLogger: INotificationAnalyticsService = {
  /**
   * Logs a notification tapped event.
   * @param params - Notification tapped parameters
   */
  logNotificationTapped: async (
    params: TNotificationTappedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.NOTIFICATION.TAPPED, params);
  },

  /**
   * Logs a notification preference updated event.
   * @param params - Notification preference updated parameters
   */
  logNotificationPreferenceUpdated: async (
    params: TNotificationPreferenceUpdatedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.NOTIFICATION.PREFERENCE_UPDATED,
      params
    );
  },
};
